import React, { useEffect, useState } from "react";
import { Trophy } from "lucide-react";
import { api } from "../lib/api";

const useCountUp = (target, duration = 1400) => {
    const [value, setValue] = useState(0);
    useEffect(() => {
        if (!target) return setValue(0);
        let start;
        let raf;
        const step = (t) => {
            if (start === undefined) start = t;
            const p = Math.min((t - start) / duration, 1);
            setValue(target * (1 - Math.pow(1 - p, 3)));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);
    return value;
};

const money = (n, digits = 0) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

export const ImpactCounter = () => {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/donations/stats").then((r) => setStats(r.data)).catch(() => setStats({ total_raised: 0, gift_count: 0, top_donors: [], monthly: [] }));
    }, []);

    const total = useCountUp(stats?.total_raised || 0);
    const monthly = stats?.monthly || [];
    const max = Math.max(...monthly.map((m) => m.amount), 1);
    const top = stats?.top_donors || [];

    return (
        <section data-testid="impact-counter" className="bg-[#0A0A0A] text-white px-6 md:px-12 lg:px-20 py-16 md:py-24 border-t border-[#D4AF37]/30">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
                <div className="lg:col-span-4">
                    <div className="eyebrow text-[#D4AF37] mb-6">Community Impact</div>
                    <div data-testid="impact-total" className="font-serif text-6xl md:text-7xl tracking-tight leading-none tabular-nums mb-3">
                        {money(total)}
                    </div>
                    <p className="text-white/55 font-light">
                        raised so far by{" "}
                        <span data-testid="impact-count" className="text-white">{stats?.gift_count ?? 0}</span>{" "}
                        {stats?.gift_count === 1 ? "gift" : "gifts"} through this page
                    </p>
                </div>

                <div className="lg:col-span-5">
                    <div className="eyebrow text-[10px] text-white/50 mb-6">Last 6 Months</div>
                    <div data-testid="impact-chart" className="flex items-end gap-3 h-40">
                        {monthly.map((m) => (
                            <div key={m.key} className="flex-1 flex flex-col items-center gap-3 h-full justify-end">
                                <span className="text-[11px] text-[#D4AF37] tabular-nums">{m.amount ? money(m.amount) : ""}</span>
                                <div
                                    className="w-full bg-[#D4AF37] transition-[height] duration-1000 ease-out"
                                    style={{ height: `${m.amount ? Math.max((m.amount / max) * 100, 4) : 2}%`, opacity: m.amount ? 1 : 0.25 }}
                                />
                                <span className="eyebrow text-[10px] text-white/50">{m.label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="lg:col-span-3">
                    <div className="eyebrow text-[10px] text-white/50 mb-6 flex items-center gap-2">
                        <Trophy size={12} className="text-[#D4AF37]" /> Top Supporters
                    </div>
                    <ol data-testid="impact-top-donors" className="space-y-4">
                        {top.length === 0 && (
                            <li className="text-white/45 font-light text-sm leading-relaxed">
                                Be the first name here — your gift starts the list.
                            </li>
                        )}
                        {top.map((d, i) => (
                            <li key={d.name} data-testid={`top-donor-${i + 1}`} className="flex items-center gap-4 border-b border-white/10 pb-4">
                                <span className="font-serif text-2xl text-[#D4AF37]/70 tabular-nums w-8">{String(i + 1).padStart(2, "0")}</span>
                                <span className="flex-1 font-serif text-lg truncate">{d.name}</span>
                                <span className="text-sm text-white/70 tabular-nums">{money(d.amount)}</span>
                            </li>
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    );
};
